import type { StaffingPlanInput, StaffingWorker } from '../staffing/public';
import type { OrganizationMemberPreview, OrganizationNode } from './model';
import { plannedOrganizationId, previewOrganizationPlan } from './organizationPlan';

export const STUDIO_WORKER_DRAG_TYPE = 'application/x-heyday-studio-workers';

export type StudioOrganizationChanges = {
  operation?: 'CREATE' | 'CHANGE' | 'END';
  incoming: number;
  outgoing: number;
  updated: number;
};

export function studioOrganizationId(reference?: { id?: string; planItemKey?: string }) {
  return reference?.planItemKey ? plannedOrganizationId(reference.planItemKey) : reference?.id;
}

export function studioWorkerOrganization(worker: StaffingWorker, plan: StaffingPlanInput) {
  return studioOrganizationId(plan.workerChanges.find(row => row.workerId === worker.id)?.organization) ?? worker.organizationId;
}

/** 조직 이동만 교체하고 개인별 직무·근무지·직함 입력은 보존합니다. */
export function moveStudioWorkers(plan: StaffingPlanInput, workers: readonly StaffingWorker[], ids: readonly string[], destination: string): StaffingPlanInput {
  const changes = new Map(plan.workerChanges.map(row => [row.workerId, row]));
  for (const worker of workers) {
    if (!ids.includes(worker.id) || studioWorkerOrganization(worker, plan) === destination) continue;
    const next = { ...(changes.get(worker.id) ?? { key: `worker:${worker.id}`, workerId: worker.id, workerDescriptor: worker.name, operation: 'JOB_CHANGE' as const }) };
    if (worker.organizationId === destination) delete next.organization;
    else next.organization = destination.startsWith('plan:') ? { planItemKey: destination.slice(5) } : { id: destination };
    const hasChange = next.organization || next.jobProfileId || next.jobLevelId || next.jobTypeId || next.locationId || next.timeType || next.workSpaceId || next.clearWorkSpace || next.managerId || next.clearManager || next.organizationAssignments || next.businessTitle !== undefined || next.businessTitleMode === 'CLEAR';
    if (hasChange) changes.set(worker.id, next);
    else changes.delete(worker.id);
  }
  return { ...plan, workerChanges: [...changes.values()] };
}

export function projectOrganizationStudio(organizations: readonly OrganizationNode[], plan: StaffingPlanInput, workers: readonly StaffingWorker[], previews?: ReadonlyMap<string, OrganizationMemberPreview>) {
  const names = new Map(workers.map(worker => [worker.id, worker.name]));
  const workersById = new Map(workers.map(worker => [worker.id, worker]));
  const nodes = previewOrganizationPlan(organizations, plan.organizationChanges, names);
  const byId = new Map(nodes.map(node => [node.id, node]));
  const changes = new Map<string, StudioOrganizationChanges>();
  const changeFor = (id: string) => {
    let change = changes.get(id);
    if (!change) {
      change = { incoming: 0, outgoing: 0, updated: 0 };
      changes.set(id, change);
    }
    return change;
  };
  for (const node of nodes) {
    if (node.pending) changeFor(node.id).operation = node.pending;
  }
  const affected = new Set<string>();
  for (const row of plan.workerChanges) {
    const worker = workersById.get(row.workerId);
    if (!worker) continue;
    const destination = studioOrganizationId(row.organization);
    if (!destination || destination === worker.organizationId) {
      // 직무·직급은 조회 모델에 ID가 없어 명시된 변경만 판정합니다.
      const titleChanged = row.businessTitleMode !== 'KEEP'
        && (row.businessTitleMode === 'CLEAR'
          ? Boolean(worker.businessTitle?.trim())
          : row.businessTitle !== undefined && row.businessTitle.trim() !== (worker.businessTitle?.trim() ?? ''));
      const updated = row.managerId || row.clearManager || row.organizationAssignments || row.jobProfileId || row.jobLevelId
        || (row.jobTypeId && row.jobTypeId !== worker.jobTypeId)
        || (row.locationId && row.locationId !== worker.locationId)
        || (row.timeType && row.timeType !== worker.timeType)
        || (row.workSpaceId && row.workSpaceId !== worker.workSpaceId)
        || (row.clearWorkSpace && Boolean(worker.workSpaceId))
        || titleChanged;
      if (worker.organizationId && updated) changeFor(worker.organizationId).updated += 1;
      continue;
    }
    if (worker.organizationId) {
      affected.add(worker.organizationId);
      changeFor(worker.organizationId).outgoing += 1;
      const source = byId.get(worker.organizationId);
      if (source) source.count = Math.max(0, source.count - 1);
    }
    affected.add(destination);
    changeFor(destination).incoming += 1;
    const target = byId.get(destination);
    if (target) target.count += 1;
  }
  const members = new Map(previews);
  for (const id of affected) {
    members.set(id, { total: byId.get(id)?.count ?? 0, members: workers.filter(worker => studioWorkerOrganization(worker, plan) === id).slice(0, 4).map(worker => ({ id: worker.id, name: worker.name, profilePhotoUrl: worker.profilePhotoUrl })) });
  }
  return { nodes, members, changes };
}

export function studioOrganizationPath(nodes: readonly OrganizationNode[], id: string) {
  const parts: string[] = [];
  const seen = new Set<string>();
  let node = nodes.find(item => item.id === id);
  while (node && !seen.has(node.id)) {
    seen.add(node.id); parts.unshift(node.name);
    node = nodes.find(item => item.id === node?.parentId);
  }
  return parts.join(' / ');
}
